import type { InventoryListByIdAPI } from '@openx/types/inventoryList.ts';
import type { AppInventoryVersionType } from '@openx/types/targeting/appInventory.ts';
import {
  type InventoryAndContentState,
  InventoryContentOption,
  type ParentAccountFeatures,
} from '@openx/types/targeting/inventoryAndContent.ts';
import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState.ts';
import { getEmptyInventoryState } from '../shared/InventoryAndContent/inventoryHelpers.ts';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen.ts';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown.ts';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly.tsx';
import { InventoryAndContentFormPreviewItems } from './InventoryAndContentItems/InventoryAndContentFormPreviewItems.tsx';
import { InventoryAndContentItems } from './InventoryAndContentItems/InventoryAndContentItems.tsx';
import type { SetInventoryListItemsDispatch } from './InventoryAndContentTopBox/constants.ts';
import { getSelectedOptionsCount } from './utils/getSelectedOptionsCount.ts';

type InventoryAndContentReadOnlyProps = TargetingReadOnlyProps<InventoryAndContentState> & {
  setInventoryListItems?: SetInventoryListItemsDispatch;
  inventoryListItems?: InventoryListByIdAPI['values'];
  parentAccountFeatures: ParentAccountFeatures;
  onRemoveParameter?: (option: InventoryContentOption) => void;
  field?: TargetingFieldsValues;
  userChangedInitialValue?: boolean;
  inventoryVersion?: AppInventoryVersionType;
};

export const InventoryAndContentReadOnly = ({
  name,
  field,
  userChangedInitialValue,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  setInventoryListItems,
  inventoryListItems,
  parentAccountFeatures,
  onRemoveParameter,
  inventoryVersion,
}: InventoryAndContentReadOnlyProps) => {
  const { isDisabledOrHasError, disabledReasonMessage } = useTargetingDisabledState(field);

  const { useCurrentInstanceState, isOa } = useTargetingContext();
  const { t } = useTranslation();

  const isEmpty = useMemo(() => {
    // check if targetingParams contains any elements
    return (
      !targetingParams ||
      !(
        Object.values(targetingParams).some(t => {
          return (t.val && Object.keys(t.val).length) || (isOa && t?.inventorylist_id);
        }) ||
        targetingParams[InventoryContentOption.INVENTORY].excludes.length ||
        targetingParams[InventoryContentOption.INVENTORY].includes.length ||
        targetingParams[InventoryContentOption.PUBLISHER_INVENTORY_RULES].length
      )
    );
  }, [targetingParams, isOa]);

  const selectedOptionsCount = useMemo(() => getSelectedOptionsCount(targetingParams), [targetingParams]);

  const { isBefInstance } = useCurrentInstanceState();

  const hasValue = selectedOptionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        isEmpty={isEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabledOrHasError}
        description={t(
          'Target specific Inventory attributes that define where and how ads will appear within the publisher ecosystem.'
        )}
        optionsCount={selectedOptionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        disabledReasonMessage={disabledReasonMessage}
      >
        {isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <InventoryAndContentFormPreviewItems
              targetingParams={targetingParams ?? getEmptyInventoryState()}
              isBefInstance={isBefInstance}
              appBundleIdDuplicateErrors={null}
              setInventoryListItems={setInventoryListItems}
              inventoryListItems={inventoryListItems}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              onRemoveParameter={onRemoveParameter}
              readonly={true}
              field={field}
              parentAccountFeatures={parentAccountFeatures}
              inventoryVersion={inventoryVersion}
              optionsCount={selectedOptionsCount}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      userChangedInitialValue={userChangedInitialValue}
      isEmpty={isEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabledOrHasError}
      description={t(
        'Target specific Inventory attributes that define where and how ads will appear within the publisher ecosystem.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {!isEmpty && targetingParams && (
        <InventoryAndContentItems
          targetingParams={targetingParams}
          isBefInstance={isBefInstance}
          appBundleIdDuplicateErrors={null}
          setInventoryListItems={setInventoryListItems}
          inventoryListItems={inventoryListItems}
          inventoryVersion={inventoryVersion}
          readonly={true}
          field={field}
        />
      )}
    </TargetingReadOnly>
  );
};
